import { Capacitor } from '@capacitor/core';
import { Settings, Camera, Loader2, CheckCircle, AlertTriangle, Eye, EyeOff, Lock, User } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { supabase } from '../../../lib/supabase';
import { logEvent } from '../../../services/loggingService';
import { getCurrentUserId } from '../../../utils/authUtils';

import JobPreferencesPanel from './JobPreferencesPanel';
import DataPrivacyTab from './UserProfile/DataPrivacyTab';
import ProfileInformationTab from './UserProfile/ProfileInformationTab';
import SecurityTab from './UserProfile/SecurityTab';
import { SettingsSection } from './UserProfile/SettingsSection';


/* ── Native Profile Form ────────────────────────────────────────────── */
const NativeUserProfile: React.FC = () => {
  const [profile, setProfile] = useState({
    name: '', email: '', phone: '', gender: '',
    street_address: '', city: '', country: '', avatar_url: '',
  });
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [uploadingAvatar, setUploading] = useState(false);
  const [avatarPreview, setAvatarPrev] = useState<string | null>(null);
  const [saveMsg, setSaveMsg]         = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const [pwd, setPwd]     = useState({ current: '', next: '', confirm: '' });
  const [showPwd, setShowPwd] = useState({ current: false, next: false, confirm: false });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMsg, setPwdMsg]       = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const userId = await getCurrentUserId();
        const { data: session } = await supabase.auth.getSession();
        const authEmail = session?.session?.user?.email ?? '';
        if (!userId) { setLoading(false); return; }
        const { data } = await supabase.from('users').select('*').eq('user_id', userId).maybeSingle();
        setProfile({
          name: data?.name ?? '',
          email: data?.email ?? authEmail,
          phone: data?.phone ?? '',
          gender: data?.gender ?? '',
          street_address: data?.street_address ?? '',
          city: data?.city ?? '',
          country: data?.country ?? '',
          avatar_url: data?.avatar_url ?? '',
        });
        if (data?.avatar_url) setAvatarPrev(data.avatar_url);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) { setSaveMsg({ type: 'err', text: 'Image must be < 5 MB' }); return; }
    setUploading(true);
    try {
      const userId = await getCurrentUserId();
      if (!userId) return;
      const ext = file.name.split('.').pop();
      const path = `avatars/${userId}-avatar-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from('avatars').upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
      const url = urlData.publicUrl;
      await supabase.from('users').update({ avatar_url: url }).eq('user_id', userId);
      setAvatarPrev(url);
      setProfile(p => ({ ...p, avatar_url: url }));
    } catch (err) {
      console.error(err);
      setSaveMsg({ type: 'err', text: 'Failed to upload photo' });
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true); setSaveMsg(null);
    try {
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('Not authenticated');
      const { error } = await supabase.from('users').update({
        name: profile.name, phone: profile.phone,
        gender: profile.gender || null,
        street_address: profile.street_address, city: profile.city, country: profile.country,
        updated_at: new Date().toISOString(),
      }).eq('user_id', userId);
      if (error) throw error;
      setSaveMsg({ type: 'ok', text: 'Profile saved successfully' });
      await logEvent({ level: 'info', action: 'profile_updated', details: {} });
    } catch (err) {
      setSaveMsg({ type: 'err', text: 'Failed to save profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPwdMsg(null);
    if (!pwd.next || pwd.next.length < 6) { setPwdMsg({ type: 'err', text: 'New password must be at least 6 characters' }); return; }
    if (pwd.next !== pwd.confirm)         { setPwdMsg({ type: 'err', text: 'Passwords do not match' }); return; }
    setPwdSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pwd.next });
      if (error) throw error;
      setPwd({ current: '', next: '', confirm: '' });
      setPwdMsg({ type: 'ok', text: 'Password updated successfully' });
    } catch (err) {
      setPwdMsg({ type: 'err', text: err instanceof Error ? err.message : 'Failed to update password' });
    } finally {
      setPwdSaving(false);
    }
  };

  /* shared styles */
  const card: React.CSSProperties = {
    background: 'var(--vscode-bg-elevated)',
    border: '1px solid var(--vscode-border-primary)',
    borderRadius: 16, padding: '16px',
    marginBottom: 14,
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  };
  const fieldWrap: React.CSSProperties = { marginBottom: 14 };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 700,
    color: 'var(--vscode-text-tertiary)',
    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5,
  };
  const inputStyle: React.CSSProperties = {
    width: '100%', minHeight: 46, padding: '10px 14px',
    borderRadius: 12, border: '1px solid var(--vscode-border-primary)',
    background: 'var(--vscode-bg-primary)', color: 'var(--vscode-text-primary)',
    fontSize: 15, boxSizing: 'border-box', outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  };
  const sectionTitle: React.CSSProperties = {
    fontSize: 12, fontWeight: 700,
    color: 'var(--vscode-text-tertiary)',
    textTransform: 'uppercase', letterSpacing: '0.07em',
    margin: '0 0 12px',
  };

  /* initials for avatar fallback */
  const initials = profile.name
    ? profile.name.split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : profile.email ? profile.email[0].toUpperCase() : '?';

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
        <Loader2 size={28} style={{ color: 'var(--vscode-primary)', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 16px 40px', background: 'var(--vscode-bg-primary)', minHeight: '100%' }}>

      {/* ── Avatar ── */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
        <div style={{ position: 'relative' }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%',
            background: avatarPreview ? 'transparent' : 'linear-gradient(135deg,#6C47FF,#4F35CC)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', boxShadow: '0 4px 16px rgba(108,71,255,0.35)',
            border: '3px solid var(--vscode-bg-elevated)',
          }}>
            {avatarPreview
              ? <img src={avatarPreview} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: 26, fontWeight: 800, color: '#fff' }}>{initials}</span>
            }
            {uploadingAvatar && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}>
                <Loader2 size={20} color="#fff" style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            )}
          </div>
          <input type="file" id="native-avatar-upload" accept="image/*" onChange={handleAvatarUpload} style={{ display: 'none' }} disabled={uploadingAvatar} />
          <label htmlFor="native-avatar-upload" style={{
            position: 'absolute', bottom: 0, right: 0,
            width: 28, height: 28, borderRadius: '50%',
            background: 'var(--vscode-primary)', border: '2px solid var(--vscode-bg-elevated)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
          }}>
            <Camera size={13} color="#fff" />
          </label>
        </div>
      </div>

      {/* ── Save message ── */}
      {saveMsg && (
        <div style={{
          marginBottom: 14, padding: '10px 14px', borderRadius: 12,
          background: saveMsg.type === 'ok' ? 'var(--vscode-success-bg)' : 'var(--vscode-error-bg)',
          border: `1px solid ${saveMsg.type === 'ok' ? 'var(--vscode-success-border)' : 'var(--vscode-error-border)'}`,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {saveMsg.type === 'ok'
            ? <CheckCircle size={15} style={{ color: 'var(--vscode-success)', flexShrink: 0 }} />
            : <AlertTriangle size={15} style={{ color: 'var(--vscode-error)', flexShrink: 0 }} />
          }
          <span style={{ fontSize: 13, color: saveMsg.type === 'ok' ? 'var(--vscode-success)' : 'var(--vscode-error)' }}>
            {saveMsg.text}
          </span>
        </div>
      )}

      {/* ── Personal Info card ── */}
      <div style={card}>
        <p style={sectionTitle}>Personal Info</p>

        <div style={fieldWrap}>
          <label style={labelStyle}>Full Name</label>
          <input style={inputStyle} value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} placeholder="Your name" />
        </div>

        <div style={fieldWrap}>
          <label style={labelStyle}>Email <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(Read-only)</span></label>
          <input style={{ ...inputStyle, color: 'var(--vscode-text-tertiary)', background: 'var(--vscode-bg-secondary)' }} value={profile.email} readOnly />
        </div>

        <div style={fieldWrap}>
          <label style={labelStyle}>Phone</label>
          <input style={inputStyle} type="tel" value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} placeholder="+1 234 567 8900" />
        </div>

        <div style={{ marginBottom: 0 }}>
          <label style={labelStyle}>Gender</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['', 'female', 'male'] as const).map((v) => {
              const label = v === '' ? 'Prefer not to say' : v === 'female' ? 'Female' : 'Male';
              const active = profile.gender === v;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => setProfile(p => ({ ...p, gender: v }))}
                  style={{
                    flex: 1, padding: '10px 6px', borderRadius: 12,
                    border: active ? '1.5px solid var(--vscode-primary)' : '1px solid var(--vscode-border-primary)',
                    background: active ? 'var(--brand-accent-bg)' : 'var(--vscode-bg-primary)',
                    color: active ? 'var(--vscode-primary)' : 'var(--vscode-text-secondary)',
                    fontSize: 12, fontWeight: active ? 700 : 500,
                    cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                    textAlign: 'center', lineHeight: 1.3,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Address card ── */}
      <div style={card}>
        <p style={sectionTitle}>Address</p>

        <div style={fieldWrap}>
          <label style={labelStyle}>Street Address</label>
          <input style={inputStyle} value={profile.street_address} onChange={e => setProfile(p => ({ ...p, street_address: e.target.value }))} placeholder="Street address" />
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 0 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>City</label>
            <input style={inputStyle} value={profile.city} onChange={e => setProfile(p => ({ ...p, city: e.target.value }))} placeholder="City" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Country</label>
            <input style={inputStyle} value={profile.country} onChange={e => setProfile(p => ({ ...p, country: e.target.value }))} placeholder="Country" />
          </div>
        </div>
      </div>

      {/* ── Save Profile button ── */}
      <button
        type="button"
        onClick={handleSaveProfile}
        disabled={saving}
        style={{
          width: '100%', padding: '14px', borderRadius: 14, border: 'none',
          background: saving ? 'var(--vscode-text-tertiary)' : 'var(--brand-cta-bg)',
          color: '#fff', fontSize: 15, fontWeight: 700, cursor: saving ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          marginBottom: 22, WebkitTapHighlightColor: 'transparent',
        }}
      >
        {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : null}
        {saving ? 'Saving…' : 'Save Profile'}
      </button>

      {/* ── Job preferences (WO-20) ── */}
      <p style={{ ...sectionTitle, marginTop: 0 }}>Job Preferences</p>
      <div style={{ marginBottom: 22 }}>
        <JobPreferencesPanel />
      </div>

      {/* ── Change Password card ── */}
      <div style={card}>
        <p style={sectionTitle}>Change Password</p>

        {pwdMsg && (
          <div style={{
            marginBottom: 12, padding: '10px 14px', borderRadius: 12,
            background: pwdMsg.type === 'ok' ? 'var(--vscode-success-bg)' : 'var(--vscode-error-bg)',
            border: `1px solid ${pwdMsg.type === 'ok' ? 'var(--vscode-success-border)' : 'var(--vscode-error-border)'}`,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            {pwdMsg.type === 'ok'
              ? <CheckCircle size={15} style={{ color: 'var(--vscode-success)', flexShrink: 0 }} />
              : <AlertTriangle size={15} style={{ color: 'var(--vscode-error)', flexShrink: 0 }} />
            }
            <span style={{ fontSize: 13, color: pwdMsg.type === 'ok' ? 'var(--vscode-success)' : 'var(--vscode-error)' }}>
              {pwdMsg.text}
            </span>
          </div>
        )}

        {(['current', 'next', 'confirm'] as const).map((k) => {
          const labels = { current: 'Current Password', next: 'New Password', confirm: 'Confirm New Password' };
          return (
            <div key={k} style={{ marginBottom: k === 'confirm' ? 0 : 14 }}>
              <label style={labelStyle}>{labels[k]}</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPwd[k] ? 'text' : 'password'}
                  value={pwd[k]}
                  onChange={e => setPwd(p => ({ ...p, [k]: e.target.value }))}
                  placeholder="••••••••"
                  style={{ ...inputStyle, paddingRight: 44 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(p => ({ ...p, [k]: !p[k] }))}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--vscode-text-tertiary)', padding: 4 }}
                >
                  {showPwd[k] ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Update Password button ── */}
      <button
        type="button"
        onClick={handleChangePassword}
        disabled={pwdSaving}
        style={{
          width: '100%', padding: '14px', borderRadius: 14, border: 'none',
          background: pwdSaving ? 'var(--vscode-text-tertiary)' : 'var(--vscode-primary)',
          color: '#fff', fontSize: 15, fontWeight: 700, cursor: pwdSaving ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {pwdSaving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Lock size={16} />}
        {pwdSaving ? 'Updating…' : 'Update Password'}
      </button>
    </div>
  );
};

/* ── Web UserProfile (unchanged tabs) ──────────────────────────────── */
const UserProfile: React.FC = () => {
  const { t } = useTranslation();
  const isNative = Capacitor.isNativePlatform();

  if (isNative) {
    return <NativeUserProfile />;
  }

  const renderProfileContent = () => (
    <div className="h-full min-h-0 overflow-y-auto overflow-x-hidden overscroll-behavior-contain scrollbar-hide">
      <div className="w-full max-w-6xl mx-auto py-2 px-3 sm:px-4 md:px-6 min-w-0 box-border">
        <div className="min-w-0">
          <ProfileInformationTab sectioned />
          <SettingsSection title="Job Preferences">
            <JobPreferencesPanel />
          </SettingsSection>
          <SecurityTab sectioned />
          <DataPrivacyTab embedded />
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col overflow-hidden min-w-0 p-1 sm:p-0">
      <div className="flex-shrink-0 mb-2 sm:mb-3 px-1 sm:px-0">
        <h1 className="text-base sm:text-lg font-semibold vscode-text-primary flex items-center gap-2 min-h-[44px] items-center">
          <Settings className="w-5 h-5 vscode-text-accent shrink-0" aria-hidden />
          {t('layout.profile.profile')}
        </h1>
      </div>

      <div className="flex-1 min-h-0 min-w-0">
        {renderProfileContent()}
      </div>
    </div>
  );
};

export default React.memo(UserProfile);
